import React, { useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { Chart as ChartComponent } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';
import {
  ICasinoReport,
  IGraphGameData,
  ISlotBucketData,
  ISlotGraphData,
} from 'appRedux/models/casinoModels';
import { getInsightDetails, getTimeFrameDetails, HeatBeanObjectKey } from 'config/config';
import { IGraphData, InsightType, TimeFrameValueEnum } from 'common/common';
import { getChartOptions } from './ChartOptions';
import { heatBeanRatings } from 'config/HeatBeanRatings';
import moment from 'moment';
import { IonSpinner } from '@ionic/react';
import { InsightFilterEnum } from 'appRedux/models/favoritesModels';
import { parseValue } from 'common/include';
import { graphTabs } from './GraphTabs';
import { useSelector } from 'react-redux';
import { IState } from 'appRedux/createStore';
import { playerTypeEnum } from 'common/commonEnum';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  annotationPlugin
);

interface InsightComboChartProps {
  timeframe: string;
  insight: string;
  playData: ICasinoReport;
  graphData?: ISlotGraphData[];
  slotSplitData: IGraphGameData[];
  onClickUpgradeNow?: () => void;
  isLoading?: boolean;
  bucketData?: ISlotBucketData;
  selectedTabId: string;
}

const formatLabel = (bucket: string, timeframe: string): string => {
  // Insight HOURS/DAYS bucket_start is casino wall-clock with a Z suffix — use UTC.
  const date = moment.utc(bucket);
  const hourlyTimeframes = [
    TimeFrameValueEnum.hours1,
    TimeFrameValueEnum.hours4,
    TimeFrameValueEnum.hours8,
    TimeFrameValueEnum.hours24,
  ];

  return hourlyTimeframes.includes(timeframe as TimeFrameValueEnum)
    ? date.format('H:mm')
    : date.format('MMM D');
};

const AnnotatedChart: React.FC<InsightComboChartProps> = ({
  timeframe,
  insight,
  playData,
  graphData,
  slotSplitData,
  onClickUpgradeNow,
  isLoading = false,
  bucketData,
  selectedTabId,
}) => {
  const isMobile = window.innerWidth < 768;
  const [chartData, setChartData] = useState<ChartData<'bar' | 'line'>>();
  const [chartOptions, setChartOptions] = useState<ChartOptions<'bar' | 'line'>>();
  const chartRef = useRef<any>(null);
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const isGraphShown = graphData && graphData.length > 0;
  const currentTab = graphTabs.find((tab) => tab.id === selectedTabId);

  const [leftAxisItem, setLeftAxisItem] = useState<IGraphData | undefined>();
  const [rightAxisItem, setRightAxisItem] = useState<IGraphData | undefined>();
  const [InsightLabel, setInsightLabel] = useState<string>('');
  const isProUser = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isProUser
  ) as boolean;
  useEffect(() => {
    const currentTab = graphTabs.find((tab) => tab.id === selectedTabId);
    if (currentTab?.primary) {
      const groupSuffix = selectedTabId.replace(/^\d+-/, '');

      let left = currentTab?.graphData.find(
        (item) => item.axes === 'left' && item.group === groupSuffix
      );
      let right = currentTab?.graphData.find(
        (item) => item.axes === 'right' && item.group === groupSuffix
      );

      if (!left) {
        left = currentTab?.graphData.find((item) => item.axes === 'left');
      }

      if (!right) {
        right = currentTab?.graphData.find((item) => item.axes === 'right');
      }

      setLeftAxisItem(left);
      setRightAxisItem(right);
    }
  }, [selectedTabId, graphTabs]);

  const getGradientStops = (data: { color: string; ntile: number }[]) => {
    const stops: { offset: string; color: string; ntile: number }[] = [];
    const total = data.length - 1;
    if (data.length === 0) return stops;
    stops.push({ offset: '0%', color: data[0].color, ntile: data[0].ntile });
    for (let i = 1; i <= total; i++) {
      const offsetPercent = (i / total) * 100;
      stops.push({
        offset: `${offsetPercent.toFixed(2)}%`,
        color: data[i].color,
        ntile: data[i].ntile,
      });
    }
    return stops;
  };

  const getLineGradient = (
    ctx: CanvasRenderingContext2D,
    chartArea: { left: number; right: number },
    stops: Array<{ offset: string; color: string; ntile: number }>
  ): CanvasGradient => {
    const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
    stops.forEach((stop) => {
      gradient.addColorStop(parseFloat(stop.offset) / 100, stop.color);
    });
    return gradient;
  };

  useEffect(() => {
    if (!graphData) return;
    const labels = graphData.map((item) => formatLabel(item.bucket, timeframe));
    const isReturnInsight = leftAxisItem?.value === InsightFilterEnum.ReturnToPlayer;

    const lineDataArray = leftAxisItem?.value
      ? graphData
        .map((item) => {
          const val = Number(item[leftAxisItem.value]);
          const ntile = item[`${leftAxisItem.value}_ntile`] ?? 0;
          const color = heatBeanRatings[ntile as HeatBeanObjectKey]?.color || '#ff1493';
          if (isNaN(val)) return undefined;
          const adjusted = isReturnInsight ? val * 100 : val;
          return {
            value: Math.round((adjusted + Number.EPSILON) * 100) / 100,
            color,
            ntile,
          };
        })
        .filter((d): d is { value: number; color: string; ntile: number } => !!d)
      : [];

    const lineData = leftAxisItem?.value
      ? graphData
        .map((item) => {
          const val = Number(item[leftAxisItem.value]);
          if (isNaN(val)) return undefined;
          const adjusted = isReturnInsight ? val * 100 : val;
          return Math.round((adjusted + Number.EPSILON) * 100) / 100;
        })
        .filter((val): val is number => typeof val === 'number')
      : [];

    const linePointColors = leftAxisItem?.value
      ? graphData.map((item) => {
        const ntile = item[`${leftAxisItem.value}_ntile`] ?? 0;
        return heatBeanRatings[ntile as HeatBeanObjectKey]?.color || '#ff1493';
      })
      : [];

    const barData = rightAxisItem?.value
      ? graphData
        .map((item) => {
          const rawValue =
              rightAxisItem.value === InsightFilterEnum.Volatility
                ? item[`${rightAxisItem.value}_ntile`]
                : item[rightAxisItem.value];

          const num = Number(rawValue);
          return isNaN(num) ? undefined : num;
        })
        .filter((val): val is number => typeof val === 'number')
      : [];

    const barColors = rightAxisItem?.value
      ? graphData.map((item) => {
        const ntile = item[`${rightAxisItem.value}_ntile`] ?? 0;
        return heatBeanRatings[ntile as HeatBeanObjectKey]?.color || '#ccc';
      })
      : [];
  
    const datasets: ChartData<'bar' | 'line'>['datasets'] = [];
    const playerType = isProUser ? playerTypeEnum.slotCheckPro : playerTypeEnum.slotCheck;
    const hasLeftAxisAccess = leftAxisItem?.playerTypeAccess?.includes(playerType) ?? false;


    const gradientStops = getGradientStops(lineDataArray);
    const borderColor = (ctx: any) => {
      const chart = ctx.chart;
      const { ctx: canvasCtx, chartArea } = chart;
      if (!chartArea) return '#ff1493'; 
      return getLineGradient(canvasCtx, chartArea, gradientStops);
    };

    if (leftAxisItem?.value && hasLeftAxisAccess) {
      datasets.push({
        type: 'line',
        label: 'RTP (%)',
        data: lineData,
        borderColor: '#ff1493',
        borderWidth: 2,
        pointRadius: 3,
        fill: false,
        pointBackgroundColor: linePointColors,
        segment: {
          borderColor: borderColor,
        },
        tension: 0.4,
        spanGaps: true,
        yAxisID: 'y',
      });
    }

    if (rightAxisItem?.displayAxesText) {
      datasets.push({
        type: 'bar',
        label: leftAxisItem?.name ?? '',
        data: barData,
        backgroundColor: barColors, 
        yAxisID: 'y1',
        barThickness: isMobile ? 4 : 8,
        maxBarThickness: isMobile ? 6 : 10,
      });
    }

    setChartData({ labels, datasets });
    const rtpMin = 0;
    const rtpMax = Math.round(Math.max(...lineData)) || 0;
    const popValue = Math.round(playData.pop);

    const y1Min = Math.min(...barData);
    const y1Max = Math.max(...barData);

    const y1Padding = (y1Max - y1Min) * 0.05;
    const y1MinPadded = Math.floor(y1Min - y1Padding);
    const y1MaxPadded = Math.ceil(y1Max + y1Padding);

    setChartOptions(
      getChartOptions(
        rtpMin,
        rtpMax,
        popValue,
        timeframe,
        y1MinPadded,
        y1MaxPadded,
        hasLeftAxisAccess,
        leftAxisItem,
        rightAxisItem
      )
    );
  }, [graphData, timeframe, insight, currentTab, leftAxisItem?.value, rightAxisItem?.value]);

  useEffect(() => {
    const closeTooltip = () => {
      const tooltip = document.getElementById('custom-tooltip');
      if (tooltip) {
        tooltip.style.opacity = '0';
        tooltip.style.display = 'none';
      }
      setClickedIndex(null);
    };

    const handleClickOutside = (event: MouseEvent) => {
      const tooltip = document.getElementById('custom-tooltip');
      if (tooltip && !tooltip.contains(event.target as Node)) {
        closeTooltip();
      }
    };

    const handleScroll = () => {
      closeTooltip();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      const tooltip = document.getElementById('custom-tooltip');
      if (tooltip && tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
    };
  }, []);

  const formatDate = (raw: ISlotGraphData) => {
    const bucketDate = moment(raw.bucket);
    if (['hours1', 'hours4', 'hours8', 'hours24'].includes(timeframe)) {
      return bucketDate.format('DD MMM YYYY, hh:mm a');
    } else {
      return bucketDate.format('DD MMM YYYY');
    }
  };

  const handleChartClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const chart = chartRef.current;
    if (
      !chart ||
      !graphData ||
      !leftAxisItem ||
      !leftAxisItem.value ||
      !rightAxisItem ||
      !rightAxisItem.value
    )
      return;

    const points = chart.getElementsAtEventForMode(
      event.nativeEvent,
      'nearest',
      { intersect: true },
      false
    );
    if (!points.length) return;

    const { index, element } = points[0];
    const raw = graphData[index];
    if (!raw) return;

    const canvasRect = chart.canvas.getBoundingClientRect();
    const left = canvasRect.left + window.pageXOffset + element.x;
    const top = canvasRect.top + window.pageYOffset + element.y - 40;

    let tooltipEl = document.getElementById('custom-tooltip');
    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.id = 'custom-tooltip';
      Object.assign(tooltipEl.style, {
        position: 'absolute',
        background: 'rgba(255, 255, 255, 1)',
        color: '#000',
        padding: '10px',
        borderRadius: '5px',
        pointerEvents: 'auto',
        fontSize: '10px',
        zIndex: '9999',
        display: 'none',
        opacity: '0',
      });
      document.body.appendChild(tooltipEl);
    }

    const isReturnInsight = leftAxisItem.value === InsightFilterEnum.ReturnToPlayer;
    const primaryRaw = raw[leftAxisItem.value];
    const adjusted = isReturnInsight ? Number(primaryRaw) * 100 : Number(primaryRaw);
    const primaryValue = isReturnInsight
      ? `${Math.round((adjusted + Number.EPSILON) * 100) / 100}%`
      : parseValue(primaryRaw, leftAxisItem.value);

    let secondaryRaw = raw[rightAxisItem.value];
    if (rightAxisItem.value === InsightFilterEnum.Volatility) {
      secondaryRaw =
        heatBeanRatings[raw[`${rightAxisItem.value}_ntile`] as HeatBeanObjectKey]
          .text_lbl_volatility ?? secondaryRaw;
    }

    const secondaryValueFormatted =
      typeof secondaryRaw === 'number' || typeof secondaryRaw === 'string'
        ? parseValue(secondaryRaw, rightAxisItem.value)
        : secondaryRaw;

    const isJackpot = insight === InsightFilterEnum.Jackpot;
    const isReturnInsightData = insight === InsightFilterEnum.ReturnToPlayer;

    let htmlContent = '';
    if (isJackpot) {
      htmlContent = `
          <div><strong>${formatDate(raw)}</strong></div>
          <div><strong>RTP:</strong> ${Math.round(Number(raw.return) * 100)}%</div>
          <div><strong>${rightAxisItem.name}:</strong> ${secondaryValueFormatted}</div>
        `;
    } else if (isReturnInsightData) {
      htmlContent = `
          <div><strong>${formatDate(raw)}</strong></div>
          <div><strong>${leftAxisItem.name}:</strong> ${primaryValue}</div>
        `;
    } else {
      htmlContent = `
          <div><strong>${formatDate(raw)}</strong></div>
          <div><strong>${leftAxisItem.name}:</strong> ${primaryValue}</div>
          <div><strong>${rightAxisItem.name}:</strong> ${secondaryValueFormatted}</div>
        `;
    }
    tooltipEl.innerHTML = htmlContent;
    Object.assign(tooltipEl.style, {
      left: `${left}px`,
      top: `${top}px`,
      opacity: '1',
      display: 'block',
    });

    setClickedIndex(index);
  };

  const getChartLabel = (bucketData: ISlotBucketData, insight: string, timeframe: string) => {
    const totalValue = bucketData.jackpot_value || 0;
    const numberOfItems = bucketData.jackpots || 0;
    const lastJackpotValue = parseValue(bucketData.last_jackpot_value || 0, 'jackpot_value');
    const lastJackpotAge = bucketData.long_jackpot_age || '';
    const transformedTimeframeLabel = timeframe;
    const label = numberOfItems === 1 ? 'Jackpot' : 'Jackpots';
    const timeFrameDetail = getTimeFrameDetails(timeframe);

    if (insight === 'jackpot_value') {
      if (totalValue) {
        return (
          <div className="chart-label">
            <strong>{numberOfItems}</strong> {label} <strong>{timeFrameDetail.label}</strong>{' '}
            <strong>= {parseValue(totalValue, 'jackpot_value')}</strong>
          </div>
        );
      }

      if (transformedTimeframeLabel === 'months1') {
        return (
          <div className="chart-label no-data">
            No jackpot was paid in the last 30 days. <br />
            The last jackpot was <strong>{lastJackpotValue}</strong>; paid{' '}
            <strong>{lastJackpotAge}</strong>.
          </div>
        );
      }

      return (
        <div className="chart-label no-data">
          No jackpot was paid in the last {timeFrameDetail.label}.<br /> The last jackpot was{' '}
          <strong>{lastJackpotValue}</strong>; paid <strong>{lastJackpotAge}</strong>.
        </div>
      );
    }

    return <div className="chart-label no-data">No data in the selected time frame</div>;
  };

  if (!isLoading && !isGraphShown) {
    return (
      <div className="no-jackpots-container slot-chart-header graph-overlay">
        {bucketData && getChartLabel(bucketData, insight, timeframe)}
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '350px' }}>
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <IonSpinner
            name="crescent"
            style={{
              width: '48px',
              height: '48px',
              color: '#4a90e2',
            }}
          />
        </div>
      ) : (
        !isLoading &&
        chartData && (
          <ChartComponent
            ref={chartRef}
            type="bar"
            data={chartData}
            options={chartOptions}
            onClick={handleChartClick}
          />
        )
      )}
    </div>
  );
};

export default AnnotatedChart;
